import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@Pages/pageRuntime/components/ui/Sheet';
import { cn } from '@Pages/pageRuntime/lib/utils';
import type React from 'react';

type SlideoverSide = 'top' | 'right' | 'bottom' | 'left';
type SlideoverSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

type SlideoverProps = {
  /**
   * Controlled open state.
   */
  open?: boolean;

  /**
   * Initial uncontrolled open state.
   */
  defaultOpen?: boolean;

  /**
   * Called when the panel opens or closes.
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Element that opens the panel.
   */
  trigger?: React.ReactNode;

  /**
   * Panel heading.
   */
  title?: React.ReactNode;

  /**
   * Supporting text below the title.
   */
  description?: React.ReactNode;

  /**
   * Panel body.
   */
  children: React.ReactNode;

  /**
   * Footer actions. The footer stacks them vertically in source order.
   */
  footer?: React.ReactNode;

  /**
   * Side the panel opens from. Defaults to `right`.
   */
  side?: SlideoverSide;

  /**
   * Panel width preset. Defaults to `md`.
   */
  size?: SlideoverSize;

  className?: string;
};

const sideSizeClasses: Record<SlideoverSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl'
};

/**
 * A side panel for details, secondary workflows, or forms. Use for master-detail pages
 * and forms that should not take over the whole page.
 *
 * @example
 * <Sdk.Components.Slideover title="Details" trigger={<Sdk.Components.Button>Open</Sdk.Components.Button>}>
 *   Content
 * </Sdk.Components.Slideover>
 */
export function Slideover({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  side = 'right',
  size = 'md',
  className
}: SlideoverProps) {
  return (
    <Sheet open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <SheetTrigger asChild>{trigger}</SheetTrigger> : null}
      <SheetContent side={side} className={cn(sideSizeClasses[size], className)}>
        {title || description ? (
          <SheetHeader>
            {title ? <SheetTitle>{title}</SheetTitle> : null}
            {description ? <SheetDescription>{description}</SheetDescription> : null}
          </SheetHeader>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto px-4">{children}</div>
        {footer ? <SheetFooter>{footer}</SheetFooter> : null}
      </SheetContent>
    </Sheet>
  );
}
